/**
 * Self-test suite: Web Push subscription (VAPID + endpoint + state machine).
 *
 * Covers:
 *   - VapidKeyValidator: base64url decode, length, leading-byte, SubtleCrypto
 *   - PushSubscriptionValidator: HTTPS-only, allow-list, malformed URL
 *   - PushSubscriptionState: state machine transitions
 *
 * Registered name: "Web Push subscription (VAPID + endpoint + state)"
 *
 * Corresponds to item 1, Section H of Signal_standard_web.md.
 *
 * Pure-logic + SubtleCrypto only. NO Service Worker or pushManager calls.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { validateVapidPublicKey } from "./VapidKeyValidator";
import {
  validateEndpoint,
  allowedPushHosts,
} from "./PushSubscriptionValidator";
import {
  transition,
  EXPIRY_WARNING_THRESHOLD_MS,
} from "./PushSubscriptionState";
import type {
  PushSubscriptionRecord,
} from "./PushSubscriptionState";

// ---------------------------------------------------------------------------
// VAPID test fixtures
// ---------------------------------------------------------------------------

/**
 * A genuine P-256 uncompressed public key generated via SubtleCrypto, encoded
 * as base64url without padding. 65 bytes, prefix 0x04.
 */
const VALID_VAPID_KEY =
  "BEyPTCYaV7ThzD5o_qGiPqm7s40uOCOSk2qbHxSjMQJAhbP0rdDLMbmgI_o-VStpFxRABJ5ro-7ise9OzTXROio";

/**
 * 64-byte base64url string — valid base64 but wrong byte length (missing the
 * 0x04 prefix byte, so only 64 bytes decoded). → wrongLength.
 */
const WRONG_LENGTH_VAPID_KEY =
  "ERQXGh0gIyYpLC8yNTg7PkFER0pNUFNWWVxfYmVoa25xdHd6fYCDhomMj5KVmJueoaSnqq2ws7a5vL_CxcjLzg";

/**
 * 65-byte key with leading byte 0x05 (not the uncompressed-point marker 0x04).
 * → notP256.
 */
const WRONG_PREFIX_VAPID_KEY =
  "BRQbIikwNz5FTFNaYWhvdn2Ei5KZoKeutbzDytHY3-bt9PsCCRAXHiUsMzpBSE9WXWRrcnmAh46VnKOqsbi_xs0";

/**
 * 65-byte key with correct 0x04 prefix but coordinates not on the P-256 curve.
 * SubtleCrypto rejects it with "Invalid keyData". → notP256.
 */
const NOT_ON_CURVE_VAPID_KEY =
  "BAMFBwkLDQ8RExUXGRsdHyEjJScpKy0vMTM1Nzk7PT9BQ0VHSUtNT1FTVVdZW11fYWNlZ2lrbW9xc3V3eXt9f4E";

/** String that contains characters outside the base64url alphabet. → badBase64. */
const BAD_BASE64_VAPID_KEY = "not-valid-base64!@#$%^&*()";

// ---------------------------------------------------------------------------
// VAPID test cases
// ---------------------------------------------------------------------------

async function testVapidValidKey(): Promise<TestCase> {
  const name = "VAPID: valid 65-byte uncompressed P-256 key → accepted";
  try {
    const result = await validateVapidPublicKey(VALID_VAPID_KEY);
    if (!result.ok) {
      return fail(name, `expected ok=true, got reason="${result.reason}"`);
    }
    if (result.keyBytes.length !== 65) {
      return fail(name, `keyBytes.length expected 65, got ${result.keyBytes.length}`);
    }
    if (result.keyBytes[0] !== 0x04) {
      return fail(name, `keyBytes[0] expected 0x04, got 0x${result.keyBytes[0].toString(16)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testVapidWrongLength(): Promise<TestCase> {
  const name = "VAPID: 64-byte key (no 0x04 prefix) → wrongLength";
  try {
    const result = await validateVapidPublicKey(WRONG_LENGTH_VAPID_KEY);
    if (result.ok) {
      return fail(name, "expected ok=false, got ok=true");
    }
    if (result.reason !== "wrongLength") {
      return fail(name, `expected reason="wrongLength", got "${result.reason}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testVapidWrongLeadingByte(): Promise<TestCase> {
  const name = "VAPID: 65-byte key with leading byte 0x05 → notP256";
  try {
    const result = await validateVapidPublicKey(WRONG_PREFIX_VAPID_KEY);
    if (result.ok) {
      return fail(name, "expected ok=false, got ok=true");
    }
    if (result.reason !== "notP256") {
      return fail(name, `expected reason="notP256", got "${result.reason}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testVapidBadBase64(): Promise<TestCase> {
  const name = "VAPID: invalid base64url → badBase64";
  try {
    const result = await validateVapidPublicKey(BAD_BASE64_VAPID_KEY);
    if (result.ok) {
      return fail(name, "expected ok=false, got ok=true");
    }
    if (result.reason !== "badBase64") {
      return fail(name, `expected reason="badBase64", got "${result.reason}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testVapidNotOnCurve(): Promise<TestCase> {
  const name = "VAPID: 65-byte 0x04-prefixed key not on P-256 curve → notP256";
  try {
    const result = await validateVapidPublicKey(NOT_ON_CURVE_VAPID_KEY);
    if (result.ok) {
      return fail(name, "SubtleCrypto accepted an invalid point — expected rejection");
    }
    if (result.reason !== "notP256") {
      return fail(name, `expected reason="notP256", got "${result.reason}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Endpoint test cases
// ---------------------------------------------------------------------------

function testEndpointFcm(): TestCase {
  const name = "Endpoint: fcm.googleapis.com/... → ok";
  const result = validateEndpoint(
    "https://fcm.googleapis.com/fcm/send/eEyJBb8fXKo:APA91bGaBC",
  );
  if (!result.ok) {
    return fail(name, `expected ok=true, got reason="${result.reason}"`);
  }
  if (result.host !== "fcm.googleapis.com") {
    return fail(name, `expected host="fcm.googleapis.com", got "${result.host}"`);
  }
  return pass(name);
}

function testEndpointHttp(): TestCase {
  const name = "Endpoint: http://... → notHttps";
  const result = validateEndpoint(
    "http://fcm.googleapis.com/fcm/send/test",
  );
  if (result.ok) {
    return fail(name, "expected ok=false, got ok=true");
  }
  if (result.reason !== "notHttps") {
    return fail(name, `expected reason="notHttps", got "${result.reason}"`);
  }
  return pass(name);
}

function testEndpointUnknownProvider(): TestCase {
  const name = "Endpoint: https://evil.com/... → unknownProvider";
  const result = validateEndpoint("https://evil.com/push/receiver");
  if (result.ok) {
    return fail(name, "expected ok=false, got ok=true");
  }
  if (result.reason !== "unknownProvider") {
    return fail(name, `expected reason="unknownProvider", got "${result.reason}"`);
  }
  return pass(name);
}

function testEndpointMalformed(): TestCase {
  const name = "Endpoint: missing scheme / malformed URL → malformed";
  const result = validateEndpoint("not-a-url-at-all");
  if (result.ok) {
    return fail(name, "expected ok=false, got ok=true");
  }
  if (result.reason !== "malformed") {
    return fail(name, `expected reason="malformed", got "${result.reason}"`);
  }
  return pass(name);
}

function testEndpointMozilla(): TestCase {
  const name = "Endpoint: updates.push.services.mozilla.com → ok";
  const result = validateEndpoint(
    "https://updates.push.services.mozilla.com/wpush/v2/gAAAAAB_test",
  );
  if (!result.ok) {
    return fail(name, `expected ok=true, got reason="${result.reason}"`);
  }
  return pass(name);
}

function testEndpointApple(): TestCase {
  const name = "Endpoint: web.push.apple.com → ok";
  const result = validateEndpoint(
    "https://web.push.apple.com/QfLbcDMDGKrfBKjfvXxxxxxx",
  );
  if (!result.ok) {
    return fail(name, `expected ok=true, got reason="${result.reason}"`);
  }
  return pass(name);
}

function testAllowedPushHostsContainsFcm(): TestCase {
  const name = "allowedPushHosts() contains fcm.googleapis.com";
  const hosts = allowedPushHosts();
  if (!hosts.has("fcm.googleapis.com")) {
    return fail(name, "fcm.googleapis.com missing from allow-list");
  }
  return pass(name, `allow-list size: ${hosts.size}`);
}

// ---------------------------------------------------------------------------
// State machine test cases
// ---------------------------------------------------------------------------

const MOCK_RECORD: PushSubscriptionRecord = {
  endpoint: "https://fcm.googleapis.com/fcm/send/abc",
  expirationTime: null,
};

function testStateSubscribeStart(): TestCase {
  const name = "State: subscribeStart from unsubscribed → subscribing";
  const next = transition("unsubscribed", { type: "subscribeStart" });
  if (next !== "subscribing") {
    return fail(name, `expected "subscribing", got "${next}"`);
  }
  return pass(name);
}

function testStateSubscribeSuccess(): TestCase {
  const name = "State: subscribing + subscribeSuccess → subscribed";
  const next = transition("subscribing", {
    type: "subscribeSuccess",
    record: MOCK_RECORD,
  });
  if (next !== "subscribed") {
    return fail(name, `expected "subscribed", got "${next}"`);
  }
  return pass(name);
}

function testStateSubscribeFail(): TestCase {
  const name = "State: subscribing + subscribeFail → unsubscribed";
  const next = transition("subscribing", {
    type: "subscribeFail",
    error: "permission denied",
  });
  if (next !== "unsubscribed") {
    return fail(name, `expected "unsubscribed", got "${next}"`);
  }
  return pass(name);
}

function testStateExpiryWarning(): TestCase {
  const name = "State: subscribed + expiryWarning → expiring";
  const next = transition("subscribed", { type: "expiryWarning" });
  if (next !== "expiring") {
    return fail(name, `expected "expiring", got "${next}"`);
  }
  return pass(name);
}

function testStateExpiringUnsubscribe(): TestCase {
  const name = "State: expiring + unsubscribe → unsubscribed";
  const next = transition("expiring", { type: "unsubscribe" });
  if (next !== "unsubscribed") {
    return fail(name, `expected "unsubscribed", got "${next}"`);
  }
  return pass(name);
}

function testStatePushReceivedNearExpiry(): TestCase {
  const name =
    "State: subscribed + pushReceived(near expiry) → expiring";
  // Construct a record that expires just below the warning threshold.
  const now = Date.now();
  const recordNearExpiry: PushSubscriptionRecord = {
    endpoint: MOCK_RECORD.endpoint,
    expirationTime: now + EXPIRY_WARNING_THRESHOLD_MS - 1000,
  };
  const next = transition("subscribed", {
    type: "pushReceived",
    now,
    record: recordNearExpiry,
  });
  if (next !== "expiring") {
    return fail(
      name,
      `expected "expiring" (expiry within threshold), got "${next}"`,
    );
  }
  return pass(name);
}

function testStatePushReceivedFarExpiry(): TestCase {
  const name =
    "State: subscribed + pushReceived(expiry far) → subscribed";
  const now = Date.now();
  const recordFarExpiry: PushSubscriptionRecord = {
    endpoint: MOCK_RECORD.endpoint,
    expirationTime: now + EXPIRY_WARNING_THRESHOLD_MS + 60_000,
  };
  const next = transition("subscribed", {
    type: "pushReceived",
    now,
    record: recordFarExpiry,
  });
  if (next !== "subscribed") {
    return fail(
      name,
      `expected "subscribed" (expiry far away), got "${next}"`,
    );
  }
  return pass(name);
}

function testStateUnsubscribeFromAny(): TestCase {
  const name = "State: unsubscribe from subscribed → unsubscribed";
  const next = transition("subscribed", { type: "unsubscribe" });
  if (next !== "unsubscribed") {
    return fail(name, `expected "unsubscribed", got "${next}"`);
  }
  return pass(name);
}

function testStateSubscribeStartFromExpiring(): TestCase {
  const name = "State: expiring + subscribeStart → subscribing (renewal)";
  const next = transition("expiring", { type: "subscribeStart" });
  if (next !== "subscribing") {
    return fail(name, `expected "subscribing", got "${next}"`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Web Push subscription (VAPID + endpoint + state)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = [
      // VAPID validation
      await testVapidValidKey(),
      await testVapidWrongLength(),
      await testVapidWrongLeadingByte(),
      await testVapidBadBase64(),
      await testVapidNotOnCurve(),
      // Endpoint validation
      testEndpointFcm(),
      testEndpointHttp(),
      testEndpointUnknownProvider(),
      testEndpointMalformed(),
      testEndpointMozilla(),
      testEndpointApple(),
      testAllowedPushHostsContainsFcm(),
      // State machine
      testStateSubscribeStart(),
      testStateSubscribeSuccess(),
      testStateSubscribeFail(),
      testStateExpiryWarning(),
      testStateExpiringUnsubscribe(),
      testStatePushReceivedNearExpiry(),
      testStatePushReceivedFarExpiry(),
      testStateUnsubscribeFromAny(),
      testStateSubscribeStartFromExpiring(),
    ];
    return buildSuiteResult(cases);
  },
});
